/**
 * Data layer for the COMPANY panel's Elite Mind section.
 *
 * Elite Mind is the athlete side of Shoorah. It produces three things: a sport
 * profile (sport and playing position), a daily private journal, and injury /
 * symptom / concussion reports.
 *
 * ── THE CONSENT RULE ────────────────────────────────────────────────────────
 * Every report carries a `share_with_team` flag the athlete sets themselves,
 * and it defaults to off. Every report figure reachable from this layer counts
 * ONLY shared reports.
 *
 * There is deliberately no "logged versus shared" number anywhere. It is the
 * obvious next metric and it is the harm: telling a club its squad logged forty
 * injuries and shared three is an invitation to lean on people, which is
 * exactly the pressure the flag exists to remove. A withheld report is not a
 * smaller number — it is not the club's number at all.
 *
 * The journal has no share control, so its CONTENTS are never reported. Not the
 * symptoms, not the sleep quality, not the muscle tension, not whether they
 * trained as planned, not the notes. Only that it is being kept, and how
 * regularly.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Backed by the company/v1 elite-mind-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter can narrow the
 * whole page — for a club, usually a squad or age group.
 *
 * The adapter is exported so a super-admin Elite Mind hub, served by the same
 * builders, can reuse the field mapping rather than restating it.
 */

import {
  getCompanyEliteMindDashboard,
  getCompanyEliteMindUsers,
  getCompanyEliteMindUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Athlete block shared by every Elite Mind dashboard. */
export const adaptEliteMind = (data) => ({
  // Squad — roster facts, not health data
  athletesRegistered: data.athletesRegistered ?? 0,
  bySport: data.bySport || [],

  // Journal: engagement only, never content
  journalEntries: data.journalEntries ?? 0,
  journalAthletes: data.journalAthletes ?? 0,
  avgEntriesPerAthlete: data.avgEntriesPerAthlete ?? 0,
  firstJournalDate: data.firstJournalDate || null,
  lastJournalDate: data.lastJournalDate || null,
  consistencySplit: data.consistencySplit || [],
  weekdayEntries: data.weekdayEntries || [],

  // Reports the athlete chose to share — and only those
  sharedReports: data.sharedReports ?? 0,
  reportingAthletes: data.reportingAthletes ?? 0,
  firstReportDate: data.firstReportDate || null,
  lastReportDate: data.lastReportDate || null,
  byReportType: data.byReportType || [],
  recentConcussions: data.recentConcussions ?? 0,
  recentConcussionAthletes: data.recentConcussionAthletes ?? 0,
  statusMix: data.statusMix || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  journalTrend: (data.dailyTrend || []).map((day) => day.entries || 0),
  athletesTrend: (data.dailyTrend || []).map((day) => day.athletes || 0),
  reportsTrend: (data.dailyTrend || []).map((day) => day.reports || 0)
});

export const fetchCompanyEliteMindDashboard = async (department = '') => {
  try {
    const data = unwrap(await getCompanyEliteMindDashboard(department));
    return {
      ...adaptEliteMind(data),
      // Company-only: the size of the population, so the page can say
      // "28 of 60 athletes keep the journal".
      totalUsers: data.totalUsers
    };
  } catch (error) {
    console.error('fetchCompanyEliteMindDashboard error:', error);
    return null;
  }
};

// Matches the EliteMindUsersTable fetcher contract.
export const fetchCompanyEliteMindUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyEliteMindUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyEliteMindUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One athlete's profile ({ user, byReportType, monthlyJournal, reportHistory }).
 * The history rows carry a date, a type and a status — never the clinical
 * detail behind them, and never a report the athlete kept private. The backend
 * 404s any user outside this company, so no scoping is needed here.
 */
export const fetchCompanyEliteMindUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyEliteMindUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyEliteMindUserDetail error:', error);
    return null;
  }
};
